import { ApiClient, ApiTypes, PolicyTypes } from '@flashbacktech/flashbackclient/api';

/**
 * Service centralisé pour charger toutes les statistiques du workspace en un seul endroit
 * Optimise les performances en réduisant le nombre d'appels API
 */

export interface WorkspaceStats {
  bucketsCount: number;
  reposCount: number;
  violationsCount: number;
  aiLlmsCount: number;
  aiApiKeysCount: number;
  hasUploadedData: boolean;
  hasRepoWithApiKey: boolean;
  totalTrafficBytes: number;
  bucketsTotalBytes: number;
  reposTotalBytes: number;
  timestamp: number;
}

const CACHE_KEY = 'workspace_stats_cache';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Store apiClient and withTokenRefresh references
let _apiClient: ApiClient | null = null;
let _withTokenRefresh: (<T>(fn: () => Promise<T>) => Promise<T>) | null = null;

export class WorkspaceStatsService {
  /**
   * Initialize the service with apiClient and withTokenRefresh
   * Must be called before using loadWorkspaceStats
   */
  static initialize(
    apiClient: ApiClient,
    withTokenRefresh: <T>(fn: () => Promise<T>) => Promise<T>
  ): void {
    _apiClient = apiClient;
    _withTokenRefresh = withTokenRefresh;
  }

  /**
   * Returns default empty stats
   */
  private static getDefaultStats(): WorkspaceStats {
    return {
      bucketsCount: 0,
      reposCount: 0,
      violationsCount: 0,
      aiLlmsCount: 0,
      aiApiKeysCount: 0,
      hasUploadedData: false,
      hasRepoWithApiKey: false,
      totalTrafficBytes: 0,
      bucketsTotalBytes: 0,
      reposTotalBytes: 0,
      timestamp: Date.now(),
    };
  }

  /**
   * Charge toutes les stats du workspace en parallèle
   */
  static async loadWorkspaceStats(
    workspaceId: string,
    forceRefresh = false
  ): Promise<WorkspaceStats> {
    if (!_apiClient || !_withTokenRefresh) {
      console.error('[WorkspaceStatsService] Service not initialized. Call initialize() first.');
      return this.getDefaultStats();
    }
    
    const apiClient = _apiClient;
    const withTokenRefresh = _withTokenRefresh;
    // CRITICAL: Guard against undefined/invalid workspaceId
    if (!workspaceId || workspaceId === 'undefined' || workspaceId === '' || workspaceId === 'ALL') {
      console.warn('[WorkspaceStatsService] Invalid workspaceId provided:', workspaceId);
      // Return default stats instead of making API calls
      const defaultStats: WorkspaceStats = {
        bucketsCount: 0,
        reposCount: 0,
        violationsCount: 0,
        aiLlmsCount: 0,
        aiApiKeysCount: 0,
        hasUploadedData: false,
        hasRepoWithApiKey: false,
        totalTrafficBytes: 0,
        bucketsTotalBytes: 0,
        reposTotalBytes: 0,
        timestamp: Date.now(),
      };
      return defaultStats;
    }

    // Vérifier le cache d'abord
    if (!forceRefresh) {
      const cached = this.getFromCache(workspaceId);
      if (cached) {
        // Dispatch immédiatement depuis le cache pour que l'UI se mette à jour instantanément
        this.dispatchStatsUpdate(cached);
        return cached;
      }
    }

    try {
      // Charger toutes les données en parallèle
      const includeParam = workspaceId && workspaceId !== 'ALL';
      const [bucketsData, reposData, violationsData] = await Promise.all([
        // Buckets
        withTokenRefresh(() => apiClient.getStorageBuckets(includeParam ? workspaceId : undefined))
          .catch(() => ({ success: false, buckets: [] } as ApiTypes.GetBucketsResponse)),

        // Repositories
        withTokenRefresh(() => apiClient.getStorageRepos(includeParam ? workspaceId : undefined))
          .catch(() => ({ success: false, repos: [] } as ApiTypes.GetReposResponse)),

        // Violations (last 7 days)
        withTokenRefresh(() => apiClient.getPolicyViolations({
          workspaceId: includeParam ? workspaceId : undefined,
          from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        }))
        .catch(() => ({ success: false, violations: [], total: 0, skip: 0, take: 0 } as PolicyTypes.GetPolicyViolationsResponse)),
      ]);

      const buckets = bucketsData?.success ? bucketsData.buckets || [] : [];
      const repos = reposData?.success ? reposData.repos || [] : [];
      const violations = violationsData?.success ? violationsData.violations || [] : [];

      // Compter les AI LLMs et AI API Keys depuis les repos
      let aiLlmsCount = 0;
      let aiApiKeysCount = 0;
      let hasRepoWithApiKey = false;

      repos.forEach((repo: { aiLlms?: unknown[]; apiKeys?: unknown[]; aiApiKeys?: unknown[] }) => {
        if (repo.aiLlms) aiLlmsCount += repo.aiLlms.length;
        const storageKeysCount = Array.isArray(repo.apiKeys) ? repo.apiKeys.length : 0;
        const aiKeysCount = Array.isArray(repo.aiApiKeys) ? repo.aiApiKeys.length : 0;
        aiApiKeysCount += aiKeysCount;
        if (storageKeysCount > 0 || aiKeysCount > 0) {
          hasRepoWithApiKey = true;
        }
      });

      // Calculer le traffic total (on pourrait charger les stats en parallèle aussi si nécessaire)
      const bucketsTotalBytes = 0; // À implémenter si nécessaire
      const reposTotalBytes = 0;
      const totalTrafficBytes = bucketsTotalBytes + reposTotalBytes;
      const hasUploadedData = totalTrafficBytes > 0;

      const stats: WorkspaceStats = {
        bucketsCount: buckets.length,
        reposCount: repos.length,
        violationsCount: violations.length,
        aiLlmsCount,
        aiApiKeysCount,
        hasUploadedData,
        hasRepoWithApiKey,
        totalTrafficBytes,
        bucketsTotalBytes,
        reposTotalBytes,
        timestamp: Date.now(),
      };

      // Sauvegarder dans le cache
      this.saveToCache(workspaceId, stats);

      // Dispatch event pour notifier les autres composants
      this.dispatchStatsUpdate(stats);

      return stats;
    } catch (error) {
      console.error('[WorkspaceStatsService] Error loading stats:', error);
      throw error;
    }
  }

  /**
   * Récupère les stats depuis le cache de manière synchrone (pour usage au reload)
   */
  static getStatsFromCache(workspaceId: string): WorkspaceStats | null {
    return this.getFromCache(workspaceId);
  }

  /**
   * Récupère les stats depuis le cache (méthode privée)
   */
  private static getFromCache(workspaceId: string): WorkspaceStats | null {
    try {
      const cached = localStorage.getItem(`${CACHE_KEY}_${workspaceId}`);
      if (cached) {
        const data = JSON.parse(cached);
        const age = Date.now() - data.timestamp;

        if (age < CACHE_DURATION) {
          return data;
        }
      }
    } catch (error) {
      console.error('[WorkspaceStatsService] Cache read error:', error);
    }
    return null;
  }

  /**
   * Sauvegarde les stats dans le cache
   */
  private static saveToCache(workspaceId: string, stats: WorkspaceStats): void {
    try {
      localStorage.setItem(`${CACHE_KEY}_${workspaceId}`, JSON.stringify(stats));

      // Compatibilité avec l'ancien système
      localStorage.setItem('bucketsTotalBytes', stats.bucketsTotalBytes.toString());
      localStorage.setItem('reposTotalBytes', stats.reposTotalBytes.toString());
      localStorage.setItem('totalTrafficBytes', stats.totalTrafficBytes.toString());
      localStorage.setItem('hasUploadedData', String(stats.hasUploadedData));
      localStorage.setItem('hasRepoWithApiKey', String(stats.hasRepoWithApiKey));
    } catch (error) {
      console.error('[WorkspaceStatsService] Cache write error:', error);
    }
  }

  /**
   * Dispatch un événement custom pour notifier les autres composants
   */
  private static dispatchStatsUpdate(stats: WorkspaceStats): void {
    try {
      window.dispatchEvent(
        new CustomEvent('workspaceStatsUpdated', {
          detail: stats,
        })
      );
    } catch (error) {
      console.error('[WorkspaceStatsService] Event dispatch error:', error);
    }
  }

  /**
   * Écoute les mises à jour des stats
   */
  static onStatsUpdate(callback: (stats: WorkspaceStats) => void): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<WorkspaceStats>;
      callback(customEvent.detail);
    };

    window.addEventListener('workspaceStatsUpdated', handler);

    return () => {
      window.removeEventListener('workspaceStatsUpdated', handler);
    };
  }

  /**
   * Invalide le cache pour forcer un refresh
   */
  static invalidateCache(workspaceId?: string): void {
    try {
      if (workspaceId) {
        localStorage.removeItem(`${CACHE_KEY}_${workspaceId}`);
      } else {
        // Invalider tous les caches
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith(CACHE_KEY)) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.error('[WorkspaceStatsService] Cache invalidation error:', error);
    }
  }
}
